import { isSchedulingQuestionKey } from "../utils/schedulingQuestions"
/**
 * RFC 0036 + RFC 0392: Agent chat synchronization hook.
 *
 * Manages conversation with the backend orchestrator via:
 * - WebSocket /ws/chat/{projectId} for real-time events (state, messages, chunks)
 * - POST /agent/connect to start a conversation (returns JSON)
 * - POST /agent/respond to answer questions (returns JSON)
 *
 * The backend runs agents as background tasks. Real-time events flow
 * through the persistent WebSocket connection.
 */

import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { toast } from 'sonner'
import { ApiRequestError, apiFetch, api, type CanvasSummary } from '../api/client'
import { useTranslation } from 'react-i18next'
import { normalizeLocale } from '../i18n/supportedLocales'
import type { DirectorSkillSummary } from '../api/directorSkills'
import type { SelectedDirectorSkillRequest } from '../generated/directorSkillTypes'
import { parseActionCardMetadata, type ActionCardMetadata } from '../generated/chatActionTypes'
import { parseDirectorTurnPresentation, type DirectorTurnPresentation } from '../generated/directorChatTypes'
import type { AutoConfirmCostSettings } from '../components/chat/autoConfirmCost'
import {
  useChatSocket,
  type ChatEvent,
  type ChatEventCanvasSummary,
  type ChatEventMessage,
  type ChatEventActivity,
  type ChatEventQuestion,
  type ChatEventQuestionMetadata,
  type ChatEventSelectedSkill,
} from './useChatSocket'
import { createDebug, createNadDebug } from '../utils/debugLog'
import { notifyProjectConfigChanged } from '../components/canvas-v2/projectSettingsEvents'
import { sanitizeNodeError } from '../utils/sanitizeNodeError'
import { trackChatPromptSubmitted } from '../analytics/chatHeaderAnalytics'

const log = createDebug('dw:chat:agent')
const eventLog = createDebug('dw:chat:event')
const thinkingLog = createDebug('dw:chat:thinking')
const dedupLog = createNadDebug('dw:chat:dedup')
const skillSubmitLog = createNadDebug('dw:director-skills:submit')
const streamPreserveLog = createNadDebug('dw:chat:stream-preserve')
const questionLog = createNadDebug('dw:chat:question')
const questionSubmitLog = createNadDebug('dw:chat:question-submit')
const freeTextAnswerSubmitLog = createNadDebug('dw:director-free-text-answers:submit')
const editResendLog = createNadDebug('dw:chat:edit-resend')
// RFC: localized Director error banners. Records that a user-facing error
// banner was surfaced via i18n (with the active locale + key) so the copy can
// be verified per-locale in NAD traces instead of shipping hardcoded English.
const i18nErrorLog = createNadDebug('dw:i18n:error-copy')
const taskStatusLog = createNadDebug('dw:chat:task-status')
const honestStopLog = createNadDebug('dw:honest-director-stop:client')
// Authoritative-state reconciliation. A Director round that ends in `tool_use`
// deliberately keeps `awaitingDirector` set and defers clearing it to the
// terminal `state`/`done` pair that follows. If that pair is never applied by
// this client, nothing else ever clears it and the chat is stuck in
// "Dreaming Now" while the backend has long since parked (see
// RFC / Workplace post 1591028932654404). This tag records the recovery.
const reconcileLog = createNadDebug('dw:chat:reconcile')
const presentationLog = createNadDebug('dw:director-chat-presentation:invalid')

/**
 * Silence, while this client believes the Director is working, after which the
 * backend's authoritative state is re-read. Long silences are NORMAL — a
 * Director waiting on a queued node batch emits no chat events for minutes —
 * so the refetch is a cheap confirmation, not an error signal: during real work
 * it returns `status: 'running'` and changes nothing.
 */
const STALE_DIRECTOR_SILENCE_MS = 45_000
/** How often the silence above is evaluated. */
const RECONCILE_POLL_MS = 15_000
type ConversationStatus = 'idle' | 'running' | 'pending_questions' | 'error'

/** Answer value sent to the API: always has a selected option, optionally custom text */
export interface AnswerValue {
  selected_option: string
  custom_text?: string
  tool_result_content?: string
}

interface QuestionResponseOptions {
  completeQuestionBatch?: boolean
  skippedQuestionIds?: string[]
  deferOptimisticQuestionAnswers?: boolean
}

function selectedOptionsSummary(answers: Record<string, AnswerValue>): string {
  return Object.entries(answers)
    .map(([key, answer]) => `${key}:${answer.selected_option}`)
    .join(',')
}

function customTextLengthsSummary(answers: Record<string, AnswerValue>): string {
  return Object.entries(answers)
    .filter(([, answer]) => Boolean(answer.custom_text))
    .map(([key, answer]) => `${key}:${answer.custom_text?.length ?? 0}`)
    .join(',')
}

function questionIdsMatch(storedQuestionId: string, submittedQuestionId: string): boolean {
  if (storedQuestionId === submittedQuestionId) return true
  const hasNumericSuffix = (candidate: string, base: string) => {
    if (!candidate.startsWith(`${base}_`)) return false
    const suffix = candidate.slice(base.length + 1)
    return suffix.length > 0 && /^\d+$/.test(suffix)
  }
  return hasNumericSuffix(storedQuestionId, submittedQuestionId)
    || hasNumericSuffix(submittedQuestionId, storedQuestionId)
}

function tracePreview(content: string): string {
  const compact = content.replace(/\s+/g, ' ').trim()
  return compact.length > 80 ? `${compact.slice(0, 77)}...` : compact
}

function traceRoles(messages: ChatMessage[]): string {
  return messages.map((message) => message.role).join(',')
}

function createEditTraceId(projectId: string, timestamp: string): string {
  const slug = `${projectId}-${timestamp}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
  return `chat-edit-${slug}`
}

interface ApiDetailPayload {
  detail?: string | { message?: string }
  error?: { message?: string }
}

function extractApiDetailMessage(payload: ApiDetailPayload | null, fallback: string): string {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return fallback
  }
  const detail = payload.detail
  if (typeof detail === 'string') {
    return detail
  }
  if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
    return detail.message
  }
  const error = payload.error
  if (error && typeof error.message === 'string') {
    return error.message
  }
  return fallback
}

async function readActionableConnectError(response: Response, fallback: string): Promise<string> {
  if (response.status !== 409 && response.status !== 503) {
    return fallback
  }
  const payload = await response.json().catch(() => null)
  return extractApiDetailMessage(payload, fallback)
}

function sanitizeChatErrorMessage(
  raw: string,
  translate: (key: string, params?: Record<string, string | number>) => string,
): string {
  return sanitizeNodeError(raw, translate, true) ?? raw
}

/** Question embedded in a message (new unified model) */
export interface EmbeddedQuestion {
  question_type: string
  tool_use_id: string
  question: string
  context?: string
  options: Array<{ value?: string; label: string; description?: string }>
  allow_custom_input?: boolean
  header?: string
  answer?: string | null
  /** Durable answer provenance supplied by authoritative state snapshots. */
  selected_option?: string | null
  custom_text?: string | null
  metadata?: ChatEventQuestionMetadata // Extra data (e.g., node details for cost confirmation)
}

export interface ChatMessage {
  metadata?: ActionCardMetadata
  actionCardError?: boolean
  presentation?: DirectorTurnPresentation
  id: string
  role: 'user' | 'assistant'
  agent?: string
  content: string
  referenced_node_ids?: string[]
  selected_skill?: ChatEventSelectedSkill
  timestamp: string
  questions?: EmbeddedQuestion[] // Optional embedded questions
  /** Backend-owned activity that continues after the Director turn ends. */
  activity?: ChatEventActivity
  /** True while this message is actively being streamed from the backend */
  isStreaming?: boolean
  /** True if this message was delivered via real streaming (not history) */
  wasStreamed?: boolean
  /** True for ephemeral system notifications (e.g. context compaction) */
  isSystemNotification?: boolean
}

type StartWithPromptOptions = {
  replayScenario?: string
  selectedSkill?: DirectorSkillSummary | Pick<DirectorSkillSummary, 'ref' | 'versionId' | 'name' | 'publicSlug' | 'displayName'>
  selectedSkillPosition?: number
  /**
   * RFC-1577: set only by the Director composer (a typed send, or its queued
   * prompt being released). Records `project_prompt_submitted` once the API
   * accepts the turn; programmatic turns leave it unset.
   */
  composerSubmission?: { attachmentCount: number }
}

function parseEmbeddedQuestion(value: ChatEventQuestion | undefined): EmbeddedQuestion | undefined {
  if (!value) return undefined
  const rawOptions = value.options ?? []
  const options = rawOptions.map((option) => ({
    value: option.value,
    label: option.label ?? option.value ?? '',
    description: option.description,
  }))
  const answerValue = value.answer
  const answer = typeof answerValue === 'string' || answerValue === null ? answerValue : undefined
  const selectedOptionValue = value.selected_option
  const selectedOption =
    typeof selectedOptionValue === 'string' || selectedOptionValue === null
      ? selectedOptionValue
      : undefined
  const customTextValue = value.custom_text
  const customText =
    typeof customTextValue === 'string' || customTextValue === null ? customTextValue : undefined

  return {
    question_type: value.question_type ?? '',
    tool_use_id: value.tool_use_id ?? '',
    question: value.question ?? '',
    context: value.context,
    options,
    allow_custom_input: value.allow_custom_input,
    header: value.header,
    answer,
    selected_option: selectedOption,
    custom_text: customText,
    metadata: value.metadata,
  }
}

function parseSelectedSkill(
  value: ChatEventSelectedSkill | undefined,
): ChatEventSelectedSkill | undefined {
  if (
    !value
    || typeof value.ref !== 'string'
    || typeof value.version_id !== 'string'
    || typeof value.name !== 'string'
    || typeof value.display_name !== 'string'
    || !Number.isInteger(value.position)
    || value.position < 0
  ) {
    return undefined
  }
  return {
    ...value,
    public_slug: typeof value.public_slug === 'string' && value.public_slug ? value.public_slug : value.name,
  }
}

function parseChatActivity(
  value: ChatEventActivity | undefined,
): ChatEventActivity | undefined {
  return value?.state === 'in_progress' ? value : undefined
}

function parseOptionalPresentation(
  value: ChatEventMessage['presentation'],
  messageId: string,
): DirectorTurnPresentation | undefined {
  if (value === undefined) return undefined
  try {
    return parseDirectorTurnPresentation(value)
  } catch (error) {
    // Cached clients may receive newer metadata. Keep the answer/questions,
    // but never render unvalidated activity or silently lose the whole event.
    const reason = error instanceof Error ? error.message : 'Presentation validation failed'
    console.error('Invalid Director presentation for message %s: %s', messageId, reason)
    presentationLog('rejected messageId=%s reason=%s', messageId, reason)
    return undefined
  }
}

function parseChatMessage(value: ChatEventMessage | undefined): ChatMessage | undefined {
  if (!value) return undefined
  const role = value.role
  if (role !== 'user' && role !== 'assistant') return undefined
  const timestamp = value.timestamp ?? new Date().toISOString()
  const id = value.id ?? `${role}-${timestamp}`
  const questions = value.questions?.map(parseEmbeddedQuestion).filter((q): q is EmbeddedQuestion => !!q)

  let metadata: ActionCardMetadata | undefined
  let actionCardError = false
  if (value.metadata !== undefined) {
    try {
      metadata = parseActionCardMetadata(value.metadata)
    } catch {
      console.error('Invalid action card metadata for message %s', id)
      actionCardError = true
    }
  }
  return {
    id,
    role,
    metadata,
    actionCardError,
    agent: value.agent,
    content: value.content ?? '',
    referenced_node_ids: value.referenced_node_ids,
    selected_skill: parseSelectedSkill(value.selected_skill),
    timestamp,
    questions,
    activity: parseChatActivity(value.activity),
    presentation: parseOptionalPresentation(value.presentation, id),
    isStreaming: value.isStreaming,
    wasStreamed: value.wasStreamed,
    isSystemNotification: value.isSystemNotification,
  }
}

function parseChatMessages(value: ChatEventMessage[] | undefined): ChatMessage[] {
  if (!Array.isArray(value)) return []
  return value.map(parseChatMessage).filter((message): message is ChatMessage => !!message)
}

function userMessageIdentity(message: ChatMessage): string {
  const skill = message.selected_skill
  return JSON.stringify([
    message.content,
    skill?.ref ?? null,
    skill?.version_id ?? null,
    skill?.position ?? null,
  ])
}

function parseCanvasSummary(value: ChatEventCanvasSummary | undefined): CanvasSummary | undefined {
  if (!value) return undefined
  if (
    typeof value.id !== 'string' ||
    typeof value.director_id !== 'string' ||
    typeof value.title !== 'string'
  ) {
    return undefined
  }
  const agentType = value.agent_type === 'dreamvibe' ? 'dreamvibe' : 'director'
  return {
    id: value.id,
    agent_type: agentType,
    agent_id: typeof value.agent_id === 'string' && value.agent_id
      ? value.agent_id
      : `${agentType}:${value.id}`,
    director_id: value.director_id,
    title: value.title,
    updated_at: typeof value.updated_at === 'string' && value.updated_at
      ? value.updated_at
      : new Date().toISOString(),
    created_at: value.created_at,
    node_count: typeof value.node_count === 'number' ? value.node_count : 0,
  }
}

/**
 * Move any in-flight `stream-*` placeholders out of the live-streaming namespace
 * (used on cancel/error). The partial text the user already saw is kept, but the
 * bubble is re-id'd and marked not-streaming so neither the `state` merge
 * (which preserves `stream-*` bubbles) nor the `chat_message` finalizer (which
 * collapses `stream-*` bubbles) treats this dead turn's placeholder as live.
 */
function sealStreamPlaceholders(messages: ChatMessage[]): ChatMessage[] {
  return messages.map((m) =>
    m.id?.startsWith('stream-')
      ? { ...m, id: `interrupted-${m.id}`, isStreaming: false }
      : m,
  )
}

/**
 * Clear the live-streaming flag on any `stream-*` placeholder while KEEPING its
 * id (used on `done`). The turn finished normally, so the bubble must stay
 * collapsible by its own finalized `chat_message` — only its claim on
 * "the Director is still streaming" is revoked.
 */
function endStreamingPlaceholders(messages: ChatMessage[]): ChatMessage[] {
  return messages.map((m) =>
    m.isStreaming && m.id?.startsWith('stream-') ? { ...m, isStreaming: false } : m,
  )
}

function conversationStatus(value: string | undefined, fallback: ConversationStatus): ConversationStatus {
  return value === 'idle' || value === 'running' || value === 'pending_questions' || value === 'error'
    ? value
    : fallback
}

export interface UseAgentChatOptions {
  // Axis 3: the persisted per-project content/output language. Sourced by the
  // caller from the project config (react-query) and sent on /agent/connect so
  // generated content (scripts/dialogue/narration) defaults to this language —
  // independent of the UI locale (axis 1) and the Director conversation language
  // (axis 2). `null`/undefined == Auto (field omitted from the request). Typed
  // `string` (not the UI's ContentLanguage) to faithfully pass through whatever
  // the backend persisted, e.g. a code outside the UI set.
  contentLanguage?: string | null
  autoConfirmCostSettings?: AutoConfirmCostSettings
  onCanvasCreated?: (canvas: CanvasSummary) => void
}

type AgentChatClientSnapshot = {
  messages: ChatMessage[]
  status: ConversationStatus
  error: string | null
  isLoading: boolean
  isRestoredSession: boolean
  awaitingDirector: boolean
  activeDirectorTools: Record<string, string>
}

const agentChatClientSnapshots = new Map<string, AgentChatClientSnapshot>()

function chatClientSnapshotKey(projectId: string, canvasId: string): string {
  return `${projectId}:${canvasId || 'agent'}`
}

export function deleteAgentChatClientSnapshot(projectId: string, canvasId: string = 'agent'): void {
  agentChatClientSnapshots.delete(chatClientSnapshotKey(projectId, canvasId))
}

export function __resetAgentChatClientSnapshotsForTests(): void {
  agentChatClientSnapshots.clear()
}

export function useAgentChat(
  projectId: string,
  canvasId: string = 'agent',
  options: UseAgentChatOptions = {},
) {
  const { i18n } = useTranslation()
  const contentLanguage = options.contentLanguage ?? null
  const autoConfirmCostSettings = options.autoConfirmCostSettings
  const onCanvasCreated = options.onCanvasCreated
  const chatScopeKey = chatClientSnapshotKey(projectId, canvasId)
  const initialSnapshot = agentChatClientSnapshots.get(chatScopeKey)
  const [messages, setMessages] = useState<ChatMessage[]>(() => initialSnapshot?.messages ?? [])
  const [status, setStatus] = useState<ConversationStatus>(() => initialSnapshot?.status ?? 'idle')
  const [error, setError] = useState<string | null>(() => initialSnapshot?.error ?? null)
  const [isLoading, setIsLoading] = useState(() => initialSnapshot?.isLoading ?? true)
  const [isRestoredSession, setIsRestoredSession] = useState(
    () => initialSnapshot?.isRestoredSession ?? true,
  )
  // RFC-0289: True between calling startWithPrompt and receiving the first
  // streaming chunk.  Used by AgentChat to show "Thinking..." correctly for
  // ALL paths that invoke the Director (send, edited prompt resend, queued prompts).
  const [awaitingDirector, setAwaitingDirector] = useState(
    () => initialSnapshot?.awaitingDirector ?? false,
  )
  const [activeDirectorTools, setActiveDirectorTools] = useState<Record<string, string>>(
    () => initialSnapshot?.activeDirectorTools ?? {},
  )
  const activeDirectorToolCount = useMemo(
    () => Object.keys(activeDirectorTools).length,
    [activeDirectorTools],
  )
  const activeChatScopeKeyRef = useRef(chatScopeKey)
  const stopAttemptRef = useRef(0)
  /**
   * Wall-clock of the last inbound chat event. `useChatSocket` swallows
   * keepalive/pong before `onEvent`, so this only advances on real events —
   * which is exactly what "the backend has told us nothing" must mean here.
   */
  const lastChatEventAtRef = useRef(Date.now())
  /**
   * Milliseconds since the last real inbound chat event. Consumers use it to
   * put a ceiling on state derived from a possibly-stale server snapshot —
   * see the cold-load Director hint in `AgentChat`. Ticked by the existing
   * reconcile poll; reset to 0 by `handleChatEvent`.
   */
  const [chatSilentMs, setChatSilentMs] = useState(0)
  /** Guards against overlapping reconciliation fetches. */
  const reconcileInFlightRef = useRef(false)
  const latestSnapshotRef = useRef<AgentChatClientSnapshot>({
    messages,
    status,
    error,
    isLoading,
    isRestoredSession,
    awaitingDirector,
    activeDirectorTools,
  })

  useEffect(() => {
    latestSnapshotRef.current = {
      messages,
      status,
      error,
      isLoading,
      isRestoredSession,
      awaitingDirector,
      activeDirectorTools,
    }
  }, [
    activeDirectorTools,
    awaitingDirector,
    error,
    isLoading,
    isRestoredSession,
    messages,
    status,
  ])

  useEffect(() => () => {
    agentChatClientSnapshots.set(activeChatScopeKeyRef.current, latestSnapshotRef.current)
  }, [])

  useEffect(() => {
    const previousScopeKey = activeChatScopeKeyRef.current
    if (previousScopeKey === chatScopeKey) return

    agentChatClientSnapshots.set(previousScopeKey, latestSnapshotRef.current)

    const cached = agentChatClientSnapshots.get(chatScopeKey)
    setMessages(cached?.messages ?? [])
    setStatus(cached?.status ?? 'idle')
    setError(cached?.error ?? null)
    setIsLoading(cached?.isLoading ?? true)
    setIsRestoredSession(cached?.isRestoredSession ?? true)
    setAwaitingDirector(cached?.awaitingDirector ?? false)
    setActiveDirectorTools(cached?.activeDirectorTools ?? {})
    activeChatScopeKeyRef.current = chatScopeKey
  }, [
    activeDirectorTools,
    awaitingDirector,
    chatScopeKey,
    error,
    isLoading,
    isRestoredSession,
    messages,
    status,
  ])

  const firstUnansweredMessageIndex = useMemo(() => {
    const allQuestionMsgs: Array<{idx: number, qCount: number, unanswered: number, toolIds: string[]}> = []
    for (let i = 0; i < messages.length; i++) {
      const questions = messages[i].questions
      if (questions?.length) {
        const unanswered = questions.filter(q => !q.answer && !isSchedulingQuestionKey(q.tool_use_id))
        allQuestionMsgs.push({
          idx: i,
          qCount: questions.length,
          unanswered: unanswered.length,
          toolIds: questions.map(q => q.tool_use_id),
        })
        if (unanswered.length > 0) {
          questionLog({
            event: 'first-unanswered-found',
            messageIndex: i,
            totalMessages: messages.length,
            questionCount: questions.length,
            unansweredCount: unanswered.length,
            toolIds: questions.map(q => q.tool_use_id).join(','),
            answers: questions.map(q => q.answer ?? 'null').join(','),
          })
          return i
        }
      }
    }
    if (allQuestionMsgs.length > 0) {
      questionLog({
        event: 'no-unanswered-questions',
        totalMessages: messages.length,
        questionMessages: allQuestionMsgs.length,
        details: JSON.stringify(allQuestionMsgs),
      })
    }
    return -1
  }, [messages])

  // Handle incoming WebSocket events
  const handleChatEvent = useCallback((event: ChatEvent) => {
    lastChatEventAtRef.current = Date.now()
    setChatSilentMs(0)
    log('event type=%s', event.type)
    const eventMessages = parseChatMessages(event.messages)
    eventLog('received type=%s status=%s msgCount=%s', event.type, event.status ?? '-', eventMessages.length || '-')
    switch (event.type) {
      case 'state': {
        const incoming = eventMessages
        thinkingLog('state event status=%s msgCount=%d', event.status, incoming.length)
        eventLog('state status=%s incomingMsgCount=%d', event.status, incoming.length)

        // NAD: trace question cards in incoming state
        const incomingQuestionMsgs = incoming.filter(m => m.questions?.length)
        if (incomingQuestionMsgs.length > 0) {
          for (const m of incomingQuestionMsgs) {
            questionLog({
              event: 'state-incoming-questions',
              status: typeof event.status === 'string' ? event.status : '',
              messageRole: m.role,
              messageContent: m.content?.slice(0, 80),
              questionCount: m.questions!.length,
              toolIds: m.questions!.map(q => q.tool_use_id).join(','),
              answers: m.questions!.map(q => q.answer ?? 'null').join(','),
              allSkipped: m.questions!.every(q => q.answer === '__skipped__'),
            })
          }
        } else {
          questionLog({
            event: 'state-no-questions',
            status: typeof event.status === 'string' ? event.status : '',
            incomingCount: incoming.length,
          })
        }

        setMessages((prev) => {
          // Preserve frontend-only `wasStreamed` flag so the Typewriter
          // doesn't re-animate messages that were already streamed live.
          const streamedTs = new Set(
            prev.filter((m) => m.wasStreamed).map((m) => m.timestamp),
          )

          // Lookups over the incoming server snapshot for dedup.
          const incomingTs = new Set(incoming.map((m) => m.timestamp))
          const incomingIds = new Set(
            incoming.map((m) => m.id).filter((id): id is string => !!id),
          )
          // Newest server timestamp in this snapshot. Gates the finalized-reply
          // preservation below (group 3) to messages the snapshot has not yet
          // caught up to — NEVER to resurrect an older reply the backend
          // deliberately dropped (e.g. context compaction shrinks history). ISO
          // UTC strings are lexicographically comparable. incoming is
          // append-ordered but reduce defensively; '' when the snapshot is empty.
          const newestIncomingTs = incoming.reduce(
            (max, m) => (m.timestamp > max ? m.timestamp : max),
            '',
          )
          // Count user-message content occurrences (optimistic dedup).
          const incomingContentCounts = new Map<string, number>()
          for (const m of incoming) {
            if (m.role === 'user') {
              const identity = userMessageIdentity(m)
              incomingContentCounts.set(identity, (incomingContentCounts.get(identity) ?? 0) + 1)
            }
          }

          // Finalized assistant content that is GENUINELY NEW in this snapshot —
          // i.e. incoming copies BEYOND what prev's finalized (non-placeholder)
          // history already had. A streamed placeholder is only superseded
          // against newly finalized text, NEVER against old assistant history
          // that merely shares a common prefix (e.g. a recurring "Sure, …"
          // opener), which would otherwise drop a live/just-ended bubble the
          // server hasn't finalized yet.
          //
          // Count-aware (not a plain content Set): when two turns finalize the
          // SAME text (e.g. both end "Done."), the second incoming copy is still
          // recognized as new — a content-only exclusion would treat it as old
          // history and leave the just-ended placeholder as a duplicate.
          const prevFinalizedCounts = new Map<string, number>()
          for (const m of prev) {
            if (m.role === 'assistant' && !m.id?.startsWith('stream-')) {
              const c = m.content.trim()
              prevFinalizedCounts.set(c, (prevFinalizedCounts.get(c) ?? 0) + 1)
            }
          }
          const newAssistantContent: string[] = []
          const consumedAssistant = new Map<string, number>()
          for (const m of incoming) {
            if (m.role !== 'assistant') continue
            const c = m.content.trim()
            if (c.length === 0) continue
            const prevCount = prevFinalizedCounts.get(c) ?? 0
            const used = consumedAssistant.get(c) ?? 0
            if (used >= prevCount) newAssistantContent.push(c)
            consumedAssistant.set(c, used + 1)
          }

          // True once the server snapshot carries a NEW finalized assistant
          // message that represents this streamed bubble. `includes` (not just
          // equality) covers two cases: the streamed (sanitized-in-80-char-
          // chunks) text differing slightly from the finalized (one-pass)
          // sanitization, and Director tool-use turns whose single finalized
          // message joins several per-round streamed segments.
          const supersededByIncoming = (content: string): boolean => {
            const trimmed = content.trim()
            if (trimmed.length === 0) return true
            return newAssistantContent.some(
              (c) => c === trimmed || c.includes(trimmed),
            )
          }

          // Preserve trailing CLIENT-ONLY messages the server snapshot doesn't
          // represent yet, in their original prev-relative ORDER. Three groups:
          //
          //  1. RFC-0289 optimistic user messages — when the user sends a
          //     message while workers are running, the backend may broadcast a
          //     state event (from node completion) before the user's POST is
          //     processed; without this the optimistic message is wiped.
          //     Content-/count-aware dedup drops copies the server already has
          //     (client and server timestamps differ).
          //
          //  2. Streamed director bubbles — actively streaming OR ended but not
          //     yet finalized into server history. The previous code preserved
          //     only `isStreaming` bubbles, so a state event landing AFTER
          //     chat_stream_end (isStreaming already false) but BEFORE the
          //     finalized chat_message dropped the bubble entirely — the text
          //     vanished until the next snapshot ("disappear then reappear").
          //     We now preserve these by stream-id until the server snapshot
          //     carries their finalized copy, and keep them in position so the
          //     stable React key updates in place instead of moving/remounting
          //     (which caused the reorder flicker).
          //
          //  3. Finalized director replies the client already collapsed from a
          //     `chat_message` (real id `assistant-<serverTs>`, wasStreamed) that
          //     a LAGGING task-launch `state` snapshot (status 'running') has not
          //     caught up to yet. Same "disappear then reappear" hazard as group
          //     2, but for the NON-`stream-` finalized bubble the group-2 filter
          //     never covered: run_nodes flips orchestrator status to 'running'
          //     and rebroadcasts conversation_history[:last_sent+1], which can
          //     momentarily trail the just-finalized reply. Preserved only while
          //     running and only when strictly newer than the snapshot's newest
          //     message, so a reply the backend deliberately dropped (context
          //     compaction) is never resurrected; deduped once a fresher snapshot
          //     carries the same synthesized `assistant-<ts>` id.
          //
          // (Preserved messages are spliced back at their original relative
          // position below, not appended — see the anchor merge.)
          const matchedCounts = new Map<string, number>()
          const preserved = prev.filter((m) => {
            // Optimistic user messages not yet acknowledged by the server.
            if (m.role === 'user' && m.id?.startsWith('user-')) {
              if (incomingTs.has(m.timestamp)) return false
              const identity = userMessageIdentity(m)
              const serverCount = incomingContentCounts.get(identity) ?? 0
              if (serverCount > 0) {
                const alreadyMatched = matchedCounts.get(identity) ?? 0
                if (alreadyMatched < serverCount) {
                  matchedCounts.set(identity, alreadyMatched + 1)
                  return false
                }
              }
              return true
            }
            // Streamed director bubbles (live or just-ended-not-yet-finalized).
            if (
              m.role === 'assistant' &&
              (m.isStreaming || (m.wasStreamed && !!m.id?.startsWith('stream-')))
            ) {
              if (m.id && incomingIds.has(m.id)) return false
              if (supersededByIncoming(m.content)) {
                streamPreserveLog({
                  event: 'placeholder-superseded',
                  messageId: m.id ?? '',
                  isStreaming: !!m.isStreaming,
                  contentLen: m.content.length,
                  incomingLen: incoming.length,
                })
                return false
              }
              return true
            }
            // Finalized director reply not yet reflected in a lagging running
            // snapshot (group 3 above). Positively match the `assistant-` id
            // shape so client-only ephemerals (`cancelled-`/`compacted-`/
            // `interrupted-`) and system notifications are NOT made sticky.
            if (
              event.status === 'running' &&
              m.role === 'assistant' &&
              !m.isSystemNotification &&
              !!m.id?.startsWith('assistant-')
            ) {
              // Already caught up by the snapshot (by id, timestamp, or finalized
              // content) — drop the client copy so there's no duplicate.
              if (
                (m.id && incomingIds.has(m.id)) ||
                incomingTs.has(m.timestamp) ||
                supersededByIncoming(m.content)
              ) {
                streamPreserveLog({
                  event: 'finalized-reply-deduped-on-catchup',
                  messageId: m.id ?? '',
                  timestamp: m.timestamp,
                  incomingLen: incoming.length,
                })
                return false
              }
              // Preserve ONLY a reply strictly newer than the snapshot's newest
              // message — i.e. one the snapshot simply hasn't reached. An older
              // reply absent from the snapshot was deliberately removed
              // (compaction) and must not be resurrected.
              if (m.timestamp > newestIncomingTs) {
                streamPreserveLog({
                  event: 'finalized-reply-preserved',
                  messageId: m.id ?? '',
                  timestamp: m.timestamp,
                  newestIncomingTs,
                  incomingLen: incoming.length,
                  prevLen: prev.length,
                })
                return true
              }
              return false
            }
            return false
          })

          const preservedUsers = preserved.filter((m) => m.role === 'user')
          const preservedStreams = preserved.filter((m) => m.role === 'assistant')

          dedupLog({
            event: 'state-dedup',
            prev: prev.length,
            incoming: incoming.length,
            optimistic: preservedUsers.length,
            incomingTsCount: incomingTs.size,
            prevUserCount: prev.filter((m) => m.role === 'user').length,
          })

          for (const m of preservedStreams) {
            streamPreserveLog({
              event: 'preserve-in-position',
              messageId: m.id ?? '',
              isStreaming: !!m.isStreaming,
              wasStreamed: !!m.wasStreamed,
              presentInIncomingById: !!(m.id && incomingIds.has(m.id)),
              contentLen: m.content.length,
              prevLen: prev.length,
              incomingLen: incoming.length,
            })
          }

          const streamedBase = streamedTs.size === 0
            ? incoming
            : incoming.map((m) =>
                streamedTs.has(m.timestamp) ? { ...m, wasStreamed: true } : m,
              )
          // A snapshot may race a newer activity/answer event. Only compare
          // matching turns; omitted historical messages must stay omitted.
          const previousTurns = new Map(prev.filter(m => m.presentation).map(m => [m.presentation!.turn_id, m]))
          const base = streamedBase.map(message => {
            if (!message.presentation) return message
            const previous = previousTurns.get(message.presentation.turn_id)
            return previous?.presentation && previous.presentation.revision > message.presentation.revision
              ? previous : message
          })

          if (preserved.length === 0) return base

          // Splice each preserved client-only message back at its ORIGINAL
          // relative position rather than appending at the end. Appending broke
          // ordering when a user sent a SECOND prompt while the previous reply
          // was still a `stream-*` placeholder: a snapshot acking the later user
          // prompt would push the unfinalized assistant bubble BELOW it, so the
          // streamed text jumped under the newer prompt and finalized in the
          // wrong slot. We anchor each preserved message immediately AFTER its
          // nearest preceding `prev` message that exists in `base` (matched by id
          // or timestamp). A preserved message with no such anchor defaults to
          // the END of `base` — the common case is a streamed reply whose
          // triggering user prompt only arrives via `incoming`, so it must follow
          // the server history, not lead it. The stable React key then updates
          // content in place. (Front insertion only happens for an empty `base`.)
          //
          // A prev message resolves to its `base` counterpart by id, then
          // timestamp, then CONTENT. The content fallback is essential: an
          // optimistic prompt acked by content (different server timestamp) or a
          // `stream-*` bubble superseded by finalized content has no id/timestamp
          // hit, and failing to advance the anchor past it would splice a LATER
          // preserved prompt ahead of the just-acknowledged server message.
          const baseIdxById = new Map<string, number>()
          const baseIdxByTs = new Map<string, number>()
          const userContentQ = new Map<string, number[]>()
          const newAssistantEntries: { idx: number; content: string }[] = []
          {
            const consumedNew = new Map<string, number>()
            base.forEach((m, idx) => {
              if (m.id && !baseIdxById.has(m.id)) baseIdxById.set(m.id, idx)
              if (!baseIdxByTs.has(m.timestamp)) baseIdxByTs.set(m.timestamp, idx)
              if (m.role === 'user') {
                const q = userContentQ.get(m.content) ?? []
                q.push(idx)
                userContentQ.set(m.content, q)
              } else if (m.role === 'assistant') {
                // Mirror `newAssistantContent`: only copies BEYOND prev's
                // finalized count count as new (and thus as supersede anchors).
                const c = m.content.trim()
                const prevCount = prevFinalizedCounts.get(c) ?? 0
                const used = consumedNew.get(c) ?? 0
                if (used >= prevCount) newAssistantEntries.push({ idx, content: c })
                consumedNew.set(c, used + 1)
              }
            })
          }
          const claimedBase = new Set<number>()
          const claimQueue = (q?: number[]): number | undefined => {
            if (!q) return undefined
            while (q.length) {
              const idx = q.shift()
              if (idx !== undefined && !claimedBase.has(idx)) return idx
            }
            return undefined
          }
          // Resolve a superseded `stream-*` placeholder to the finalized assistant
          // message that supersedes it. MUST mirror `supersededByIncoming`
          // (exact OR containing), so a placeholder superseded by CONTAINMENT
          // still advances the anchor to its finalized reply rather than leaving
          // a later optimistic prompt spliced ahead of it.
          const claimAssistant = (trimmed: string): number | undefined => {
            for (const e of newAssistantEntries) {
              if (claimedBase.has(e.idx)) continue
              if (e.content === trimmed || e.content.includes(trimmed)) return e.idx
            }
            return undefined
          }
          const preservedSet = new Set(preserved)
          const insertsAfter = new Map<number, ChatMessage[]>() // -1 = front
          let lastAnchor = base.length - 1
          for (const m of prev) {
            if (preservedSet.has(m)) {
              const bucket = insertsAfter.get(lastAnchor) ?? []
              bucket.push(m)
              insertsAfter.set(lastAnchor, bucket)
              continue
            }
            let baseIdx: number | undefined
            const byId = m.id ? baseIdxById.get(m.id) : undefined
            if (byId !== undefined && !claimedBase.has(byId)) {
              baseIdx = byId
            } else {
              const byTs = baseIdxByTs.get(m.timestamp)
              if (byTs !== undefined && !claimedBase.has(byTs)) {
                baseIdx = byTs
              } else if (m.role === 'user') {
                baseIdx = claimQueue(userContentQ.get(m.content))
              } else if (m.role === 'assistant') {
                baseIdx = claimAssistant(m.content.trim())
              }
            }
            if (baseIdx !== undefined) {
              claimedBase.add(baseIdx)
              lastAnchor = baseIdx
            }
          }
          const result: ChatMessage[] = [...(insertsAfter.get(-1) ?? [])]
          base.forEach((m, idx) => {
            result.push(m)
            const bucket = insertsAfter.get(idx)
            if (bucket) result.push(...bucket)
          })
          return result
        })
        const eventStatus = conversationStatus(event.status, 'idle')
        setStatus(eventStatus)
        if (eventStatus !== 'running') {
          setAwaitingDirector(false)
          setActiveDirectorTools({})
          // The turn is over. Any `stream-*` placeholder the merge above kept
          // was not superseded by finalized content — typically a turn that
          // errored or was cancelled while the chat socket was down, so its
          // `error`/`cancelled` event never arrived. Seal it like those events
          // would, or it keeps claiming the Director is streaming and a later
          // turn's finalizer captures it.
          setMessages(sealStreamPlaceholders)
        }
        if (eventStatus !== 'error') {
          setError(null)
        }
        setIsLoading(false)

        // Surface BYOM model load failures as user-visible warnings
        if (event.byom_warnings && Array.isArray(event.byom_warnings)) {
          for (const warning of event.byom_warnings) {
            toast.warning(warning, { duration: 8000 })
          }
        }
        break
      }

      case 'workflow':
        if (event.error) {
          setError(i18n.t('chat:errors.canvasError', { detail: event.error }))
        }
        break

      case 'chat_message':
        setIsRestoredSession(false)
        {
          const chatMsg = parseChatMessage(event)
          if (chatMsg?.questions?.length) {
            questionLog({
              event: 'chat-message-with-questions',
              role: chatMsg.role,
              questionCount: chatMsg.questions.length,
              toolIds: chatMsg.questions.map(q => q.tool_use_id).join(','),
              answers: chatMsg.questions.map(q => q.answer ?? 'null').join(','),
            })
          }
        }
        setMessages((prev) => {
          const chatMsg = parseChatMessage(event)
          if (!chatMsg) return prev
          const stableIdIndex = prev.findIndex((message) => message.id === chatMsg.id || (
            chatMsg.presentation && message.presentation?.turn_id === chatMsg.presentation.turn_id
          ))
          if (stableIdIndex >= 0) {
            const previous = prev[stableIdIndex]
            if (previous.presentation && chatMsg.presentation && previous.presentation.revision > chatMsg.presentation.revision) return prev
            const updated = [...prev]
            updated[stableIdIndex] = {
              ...chatMsg,
              presentation: event.presentation === undefined ? previous.presentation : chatMsg.presentation,
              wasStreamed: prev[stableIdIndex].wasStreamed || chatMsg.wasStreamed,
            }
            return updated
          }
          if (chatMsg.role === 'user') {
            // Primary: backward scan for adjacent optimistic user message
            for (let i = prev.length - 1; i >= 0; i--) {
              const m = prev[i]
              if (
                m.role === 'user' &&
                m.id &&
                (m.id.startsWith('optimistic-answer-') || m.id.startsWith('user-'))
              ) {
                const updated = [...prev]
                updated[i] = chatMsg
                return updated
              }
              if (m.role !== 'user') break
            }
            // Fallback: scan ALL messages for any optimistic with matching
            // content. This handles the case where an assistant message (e.g.
            // streaming chunk) arrived between the optimistic and the end of
            // the array, causing the backward scan to break early.
            const eventContent = chatMsg.content
            const optimisticIdx = prev.findIndex(
              (m) =>
                m.role === 'user' &&
                m.id?.startsWith('user-') &&
                m.content === eventContent,
            )
            if (optimisticIdx >= 0) {
              dedupLog({ event: 'chat-message-fallback', optimisticIdx: optimisticIdx })
              const updated = [...prev]
              updated[optimisticIdx] = chatMsg
              return updated
            }
          }

          if (chatMsg.role === 'assistant' && (chatMsg.agent === 'director' || !chatMsg.agent)) {
            // Collapse ALL `stream-*` placeholders (scanning the WHOLE list, not
            // just the trailing assistant suffix) into the single finalized
            // message. Director tool-use turns emit one `stream-*` id per LLM
            // round but finalize as ONE combined chat_message; and the
            // position-preserving state merge can leave a placeholder BEFORE a
            // later user prompt — a trailing-only scan would miss it and append a
            // duplicate in the wrong slot. Director turns finalize sequentially,
            // so any pending placeholders belong to this final. The final is
            // inserted at the FIRST placeholder's slot so it keeps its position.
            const placeholderIdxs: number[] = []
            for (let i = 0; i < prev.length; i++) {
              const m = prev[i]
              if (m.role === 'assistant' && m.wasStreamed && m.id?.startsWith('stream-')) {
                placeholderIdxs.push(i)
              }
            }
            if (placeholderIdxs.length > 0) {
              const insertAt = placeholderIdxs[0]
              const drop = new Set(placeholderIdxs)
              const updated: ChatMessage[] = []
              for (let i = 0; i < prev.length; i++) {
                if (i === insertAt) updated.push({ ...chatMsg, wasStreamed: true })
                if (drop.has(i)) continue
                updated.push(prev[i])
              }
              return updated
            }
          }

          const existingIndex = prev.findIndex((m) => m.timestamp === chatMsg.timestamp)
          if (existingIndex >= 0) {
            const updated = [...prev]
            updated[existingIndex] = chatMsg
            return updated
          }
          return [...prev, chatMsg]
        })
        break

      case 'chat_chunk':
        setIsRestoredSession(false)
        {
          const chunkMessageId = typeof event.message_id === 'string' ? event.message_id : ''
          const chunkContent = typeof event.content === 'string' ? event.content : ''
          log('chunk msgId=%s len=%d', chunkMessageId, chunkContent.length)
        }
        setMessages((prev) => {
          const msgId = typeof event.message_id === 'string' ? event.message_id : ''
          const chunk = typeof event.content === 'string' ? event.content : ''
          if (!msgId) return prev
          const existingIndex = prev.findIndex((m) => m.id === msgId)
          if (existingIndex >= 0) {
            const updated = [...prev]
            updated[existingIndex] = {
              ...updated[existingIndex],
              content: updated[existingIndex].content + chunk,
            }
            return updated
          }
          return [
            ...prev,
            {
              id: msgId,
              role: 'assistant',
              agent: 'director',
              content: chunk,
              timestamp: new Date().toISOString(),
              isStreaming: true,
              wasStreamed: true,
            },
          ]
        })
        break

      case 'chat_stream_end':
        log('stream_end msgId=%s has_tool_use=%s', event.message_id, event.has_tool_use)
        thinkingLog('chat_stream_end -> isStreaming=false for msgId=%s has_tool_use=%s',
          event.message_id, event.has_tool_use)
        setMessages((prev) => {
          const msgId = typeof event.message_id === 'string' ? event.message_id : ''
          if (!msgId) return prev
          const existingIndex = prev.findIndex((m) => m.id === msgId)
          if (existingIndex >= 0) {
            const updated = [...prev]
            updated[existingIndex] = {
              ...updated[existingIndex],
              isStreaming: false,
            }
            return updated
          }
          return prev
        })
        // Only clear awaitingDirector when the LLM round has no tool_use,
        // meaning this is the final round. Director tool execution is tracked
        // separately by director_tool_activity and counted in the task banner.
        if (event.has_tool_use !== true) {
          thinkingLog('chat_stream_end -> no tool_use, clearing awaitingDirector')
          setAwaitingDirector(false)
        } else {
          thinkingLog('chat_stream_end -> has tool_use, keeping awaitingDirector=true')
        }
        break

      case 'director_tool_activity': {
        const toolUseId = typeof event.tool_use_id === 'string' && event.tool_use_id
          ? event.tool_use_id
          : 'unknown-tool'
        const toolName = typeof event.tool_name === 'string' && event.tool_name
          ? event.tool_name
          : 'Director tool'
        const isRunning = event.status === 'running'
        taskStatusLog({
          event: 'director-tool-activity',
          trace_id: `director-tool:${projectId}`,
          projectId,
          status: isRunning ? 'running' : 'idle',
          toolUseId,
          toolName,
        })
        setActiveDirectorTools((prev) => {
          if (isRunning) {
            return { ...prev, [toolUseId]: toolName }
          }
          if (!(toolUseId in prev)) return prev
          const next = { ...prev }
          delete next[toolUseId]
          return next
        })
        break
      }

      case 'status': {
        const newStatus = conversationStatus(event.status, status)
        thinkingLog('status event newStatus=%s (prev=%s)', newStatus, status)
        eventLog('status-change old=%s new=%s', status, newStatus)
        setStatus(newStatus)
        break
      }

      case 'done': {
        const doneStatus = conversationStatus(event.status, 'idle')
        thinkingLog('done event doneStatus=%s -> awaitingDirector=false', doneStatus)
        eventLog('done status=%s', doneStatus)
        setStatus(doneStatus)
        setAwaitingDirector(false)
        setActiveDirectorTools({})
        // `done` means the turn is over, so no bubble may still claim
        // `isStreaming` — a surviving one keeps `isDirectorActive` true on its
        // own and re-creates the stuck "Dreaming Now" spinner even after
        // awaitingDirector clears (a missed `chat_stream_end` is enough).
        // Unlike `error`/`cancelled` this does NOT re-id the bubble out of the
        // `stream-` namespace: the turn ended normally, so its own finalized
        // `chat_message` must still be able to collapse the placeholder rather
        // than append a duplicate beside it.
        setMessages(endStreamingPlaceholders)
        break
      }

      case 'error':
        thinkingLog('error event -> awaitingDirector=false, msg=%s', event.message)
        eventLog('error msg=%s', event.message)
        setError(typeof event.message === 'string'
          ? sanitizeChatErrorMessage(
              event.message,
              (key, params) => String(i18n.t(`canvas:${key}`, params)),
            )
          : i18n.t('chat:errors.unknown'))
        setStatus('error')
        setAwaitingDirector(false)
        setActiveDirectorTools({})
        // Seal any in-flight stream placeholder: the turn died without a
        // finalized chat_message, so the bubble must leave the `stream-*`
        // namespace. Otherwise a LATER turn's chat_message would treat this
        // stale placeholder as its own (the finalizer scans the whole list) and
        // render the new answer in the dead turn's slot.
        setMessages(sealStreamPlaceholders)
        break

      case 'cancelled':
        thinkingLog('cancelled event -> awaitingDirector=false')
        eventLog('cancelled')
        setStatus('idle')
        setAwaitingDirector(false)
        setActiveDirectorTools({})
        setMessages((prev) => [
          // Seal the cancelled turn's stream placeholder (see `error`).
          ...sealStreamPlaceholders(prev),
          {
            id: `cancelled-${Date.now()}`,
            role: 'assistant',
            content: typeof event.message === 'string' ? event.message : 'Execution stopped.',
            timestamp: new Date().toISOString(),
          },
        ])
        break

      case 'context_compacted':
        setMessages((prev) => [
          ...prev,
          {
            id: `compacted-${Date.now()}`,
            role: 'assistant',
            content: 'Compacting conversation history\u2026',
            timestamp: new Date().toISOString(),
            isSystemNotification: true,
          },
        ])
        break

      case 'node_status':
        // Cross-instance node status sync: when the node ran on a different
        // API instance, the YDoc write was local to that instance.  This
        // event arrives via Redis Pub/Sub so we can update the local YDoc.
        // Dispatch a DOM event so useYCanvas can apply it without prop threading.
        if (typeof event.node_id === 'string' && typeof event.status === 'string') {
          const hasError = Object.prototype.hasOwnProperty.call(event, 'error')
          const error =
            typeof event.error === 'string'
              ? event.error
              : event.error === null
                ? null
                : undefined
          const hasAction = Object.prototype.hasOwnProperty.call(event, 'action')
          const action =
            typeof event.action === 'string'
              ? event.action
              : event.action === null
                ? null
                : undefined
          window.dispatchEvent(
            new CustomEvent('dw:node-status', {
              detail: {
                nodeId: event.node_id,
                status: event.status,
                ...(hasError ? { error } : {}),
                ...(hasAction ? { action } : {}),
              },
            }),
          )
        }
        break

      case 'canvas_created': {
        const canvas = parseCanvasSummary(event.canvas)
        if (canvas) {
          onCanvasCreated?.(canvas)
        }
        break
      }

      case 'project_config_changed': {
        // Committed Global Config write (user PATCH or Director
        // update_project_config) — refresh every useProjectConfig consumer.
        notifyProjectConfigChanged({
          projectId,
          canvasId: event.canvas_id ?? canvasId ?? 'agent',
          changedFields: event.changed_fields ?? [],
          actor: event.actor ?? null,
          autoConfirmOff: (event.auto_confirm_off ?? []).filter(
            (cls): cls is 'image' | 'video' => cls === 'image' || cls === 'video',
          ),
        })
        break
      }
    }
  }, [canvasId, projectId, status, i18n, onCanvasCreated])

  // Connect WebSocket for real-time events
  const {
    connected: chatSocketConnected,
    connectionState: chatConnectionState,
  } = useChatSocket({
    projectId,
    canvasId,
    enabled: !!projectId,
    onEvent: handleChatEvent,
  })

  // Authoritative-state reconciliation.
  //
  // The console's chat state is a CACHE; `WorkflowState.conversation_history`
  // on the backend is the source of truth. A Director round ending in
  // `tool_use` (`ask_user_question` is one) keeps `awaitingDirector` set on
  // purpose and defers clearing it to the terminal `state`/`done` pair. Today,
  // if this client never applies that pair, nothing re-reads the backend while
  // the socket stays open — the sidebar shows "Dreaming Now" forever even
  // though the orchestrator parked minutes ago (Workplace post
  // 1591028932654404: 3h47m parked in `pending_questions`).
  //
  // So: whenever this client believes the Director is working but has heard
  // nothing for STALE_DIRECTOR_SILENCE_MS, re-read `GET /agent/state` — the
  // same read-only snapshot the chat WebSocket sends on connect — and apply it
  // through the normal `state` handler, which owns the merge against
  // optimistic/streamed bubbles and clears `awaitingDirector` when the
  // authoritative status is not `running`. During genuine long work (a queued
  // node batch, a slow provider) it returns `running` and changes nothing.
  const reconcileDirectorState = useCallback(async () => {
    if (!projectId || reconcileInFlightRef.current) return

    const snapshot = latestSnapshotRef.current
    const believesDirectorActive =
      snapshot.awaitingDirector || snapshot.messages.some((m) => m.isStreaming)
    if (!believesDirectorActive) return

    const silentMs = Date.now() - lastChatEventAtRef.current
    if (silentMs < STALE_DIRECTOR_SILENCE_MS) return

    reconcileLog({
      event: 'stale-detected',
      trace_id: `chat-reconcile:${projectId}`,
      projectId,
      canvasId,
      silentMs,
      statusBefore: snapshot.status,
      awaitingDirector: snapshot.awaitingDirector,
      streamingBubbles: snapshot.messages.filter((m) => m.isStreaming).length,
    })

    reconcileInFlightRef.current = true
    try {
      const response = await apiFetch(
        `/projects/${projectId}/agentic/agent/state?canvas_id=${encodeURIComponent(canvasId)}`,
        { retryTransient: false },
      )
      if (!response.ok) {
        reconcileLog({
          event: 'refetch-failed',
          trace_id: `chat-reconcile:${projectId}`,
          projectId,
          httpStatus: response.status,
        })
        return
      }
      const authoritative = await response.json()
      reconcileLog({
        event: 'refetch-applied',
        trace_id: `chat-reconcile:${projectId}`,
        projectId,
        canvasId,
        statusBefore: snapshot.status,
        statusAfter: typeof authoritative?.status === 'string' ? authoritative.status : 'unknown',
        messageCount: Array.isArray(authoritative?.messages) ? authoritative.messages.length : 0,
        pendingQuestionCount: Array.isArray(authoritative?.pending_questions)
          ? authoritative.pending_questions.length
          : 0,
      })
      handleChatEvent({ ...authoritative, type: 'state' })
    } catch (err) {
      reconcileLog({
        event: 'refetch-error',
        trace_id: `chat-reconcile:${projectId}`,
        projectId,
        error: err instanceof Error ? err.message : String(err),
      })
    } finally {
      reconcileInFlightRef.current = false
      // Restart the silence window even on failure so a persistently failing
      // refetch retries on the poll interval instead of every tick.
      lastChatEventAtRef.current = Date.now()
    }
  }, [projectId, canvasId, handleChatEvent])

  useEffect(() => {
    // The timer runs whenever there is a project, NOT only while the socket is
    // connected. `chatSilentMs` is a staleness clock, and a disconnect is
    // exactly when state goes stale — gating the whole effect on connectivity
    // would freeze the clock at the moment it matters most. A dropped
    // socket leaves `status` at `running` until the reconnect snapshot
    // arrives, so consumers relying on the clock to expire would otherwise
    // wait forever. Reconciliation still requires a live socket.
    if (!projectId) return
    const timer = setInterval(() => {
      // Piggy-backs on the existing reconcile poll rather than adding a second
      // timer: four state writes per minute, and only when the value moves.
      setChatSilentMs(Date.now() - lastChatEventAtRef.current)
      if (chatSocketConnected) void reconcileDirectorState()
    }, RECONCILE_POLL_MS)
    return () => clearInterval(timer)
  }, [projectId, chatSocketConnected, reconcileDirectorState])

  // Start a new conversation with a prompt (via POST /agent/connect)
  const startWithPrompt = useCallback(
    async (
      prompt: string,
      referencedNodeIds?: string[],
      partialAnswers?: Record<string, string>,
      options: StartWithPromptOptions = {},
    ) => {
      // RFC-0289: Backend accepts prompts during "running" state (workers
      // in-progress) so we no longer block here.  The Director lock on the
      // backend serialises concurrent invocations.
      if (!projectId) return

      // Show user's prompt in chat immediately (optimistic)
      if (prompt.trim()) {
        setMessages((prev) => [
          ...prev,
          {
            id: `user-${Date.now()}`,
            role: 'user',
            content: prompt,
            referenced_node_ids:
              referencedNodeIds && referencedNodeIds.length > 0 ? referencedNodeIds : undefined,
            selected_skill: options.selectedSkill
              ? {
                  ref: options.selectedSkill.ref,
                  version_id: options.selectedSkill.versionId,
                  name: options.selectedSkill.name,
                  public_slug: options.selectedSkill.publicSlug,
                  display_name: options.selectedSkill.displayName,
                  position: options.selectedSkillPosition ?? 0,
                }
              : undefined,
            timestamp: new Date().toISOString(),
          },
        ])
      }

      setStatus('running')
      setAwaitingDirector(true)
      thinkingLog('startWithPrompt -> status=running, awaitingDirector=true')
      setError(null)

      try {
        const body: {
          prompt: string
          referenced_node_ids?: string[]
          partial_answers?: Record<string, string>
          replay_scenario?: string
          selected_skill?: SelectedDirectorSkillRequest
          selected_skill_position?: number
          conversation_language?: string
          canvas_id: string
          content_language?: string
          auto_confirm_cost_settings?: AutoConfirmCostSettings
        } = {
          prompt,
          canvas_id: canvasId,
          // Axis 2: Director conversation language follows the user's UI locale
          // (react-i18next). Backend injects matching cultural priors (register,
          // honorifics) into the Director system prompt. See PR #2105.
          conversation_language: normalizeLocale(i18n.language) ?? 'en',
        }
        // Axis 3: seed the content/output language from the PERSISTED project
        // setting (NOT the UI locale). Only sent when set; the backend connect
        // seed is guarded by `if request.content_language:` so it never clobbers
        // a persisted value with null.
        if (contentLanguage) {
          body.content_language = contentLanguage
        }
        if (referencedNodeIds && referencedNodeIds.length > 0) {
          body.referenced_node_ids = referencedNodeIds
        }
        if (partialAnswers && Object.keys(partialAnswers).length > 0) {
          body.partial_answers = partialAnswers
        }
        if (options.replayScenario) {
          body.replay_scenario = options.replayScenario
        }
        if (options.selectedSkill) {
          body.selected_skill = {
            ref: options.selectedSkill.ref,
            version_id: options.selectedSkill.versionId,
          }
          body.selected_skill_position = options.selectedSkillPosition ?? 0
        }
        if (autoConfirmCostSettings) {
          body.auto_confirm_cost_settings = autoConfirmCostSettings
        }

        skillSubmitLog({
          event: 'director-turn-submitted', feature: 'director-skills', project_id: projectId,
          skill_ref: body.selected_skill?.ref ?? null,
          skill_version_id: body.selected_skill?.version_id ?? null,
          inline_position: body.selected_skill_position ?? null,
        })
        const response = await apiFetch(
          `/projects/${projectId}/agentic/agent/connect`,
          {
            method: 'POST',
            body: JSON.stringify(body),
            retryTransient: 'admission-only',
          }
        )

        if (!response.ok) {
          console.error('[useAgentChat] /agent/connect failed:', response.status)
          const fallback = i18n.t('chat:errors.directorUnreachable')
          const message = await readActionableConnectError(response, fallback)
          i18nErrorLog({
            event: 'localized-error-banner',
            path: 'connect',
            key: 'chat:errors.directorUnreachable',
            locale: i18n.language,
            status: response.status,
          })
          throw new Error(message)
        }

        if (options.composerSubmission) {
          trackChatPromptSubmitted({
            projectId,
            canvasId,
            selectedSkill: body.selected_skill,
            referencedNodeIds: body.referenced_node_ids,
            attachmentCount: options.composerSubmission.attachmentCount,
          })
        }

        // JSON response — events come via WebSocket.
        // Log if the backend didn't actually spawn the Director (prompt silently ignored).
        const data = await response.json()
        if (prompt.trim() && data && !data.ok) {
          console.warn('[useAgentChat] /agent/connect did not spawn Director:', data.status)
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return
        }
        console.error('Failed to start agent:', err)
        setError(err instanceof Error ? err.message : i18n.t('chat:errors.failedToStart'))
        setStatus('idle')
        setAwaitingDirector(false)
        setActiveDirectorTools({})
      }
    },
    [projectId, canvasId, i18n, contentLanguage, autoConfirmCostSettings]
  )

  // Respond to pending questions (via POST /agent/respond)
  const respondToQuestions = useCallback(
    async (
      answers: Record<string, AnswerValue>,
      options?: QuestionResponseOptions,
    ) => {
      if (!projectId) {
        return
      }

      const answerKeys = Object.keys(answers)
      const completeQuestionBatch = options?.completeQuestionBatch === true
      const deferOptimisticQuestionAnswers =
        completeQuestionBatch || options?.deferOptimisticQuestionAnswers === true
      const skippedQuestionIds = new Set(options?.skippedQuestionIds ?? [])
      const rollbackOptimisticQuestionAnswers = () => {
        if (deferOptimisticQuestionAnswers) return
        setMessages((previous) => previous.map((message) => {
          if (!message.questions) return message
          const questions = message.questions.map((question) => {
            const key = question.tool_use_id
            const submittedAnswer = key ? answers[key] : undefined
            const optimisticAnswer = submittedAnswer
              ? (submittedAnswer.custom_text || submittedAnswer.selected_option)
              : skippedQuestionIds.has(key) ? '__skipped__' : undefined
            if (optimisticAnswer === undefined || question.answer !== optimisticAnswer) {
              return question
            }
            return {
              ...question,
              answer: null,
              selected_option: undefined,
              custom_text: undefined,
            }
          })
          return questions.some((question, index) => question !== message.questions![index])
            ? { ...message, questions }
            : message
        }))
      }
      const reconcileQuestionAnswers = async (
        requireSubmittedAnswersPersisted = true,
      ): Promise<boolean> => {
        try {
          const response = await apiFetch(
            `/projects/${projectId}/agentic/agent/state?canvas_id=${encodeURIComponent(canvasId)}`,
            { retryTransient: false },
          )
          if (!response.ok) return false

          const authoritative = await response.json()
          if (deferOptimisticQuestionAnswers && requireSubmittedAnswersPersisted) {
            const unmatchedAuthoritativeQuestions = parseChatMessages(authoritative?.messages)
              .flatMap((message) => message.questions ?? [])
            const takeAuthoritativeQuestion = (questionId: string) => {
              let questionIndex = unmatchedAuthoritativeQuestions.findIndex(
                (question) => question.tool_use_id === questionId,
              )
              if (questionIndex < 0) {
                questionIndex = unmatchedAuthoritativeQuestions.findIndex((question) =>
                  questionIdsMatch(question.tool_use_id, questionId),
                )
              }
              if (questionIndex < 0) return undefined
              const [question] = unmatchedAuthoritativeQuestions.splice(questionIndex, 1)
              return question
            }
            const submittedAnswersPersisted = answerKeys.every((questionId) => {
              const question = takeAuthoritativeQuestion(questionId)
              const submitted = answers[questionId]
              if (!question || !submitted || !question.answer) return false
              if (question.selected_option !== submitted.selected_option) return false
              return submitted.custom_text === undefined
                || question.custom_text === submitted.custom_text
            })
            const skippedAnswersPersisted = [...skippedQuestionIds].every(
              (questionId) => takeAuthoritativeQuestion(questionId)?.answer === '__skipped__',
            )
            const batchPersisted = submittedAnswersPersisted && skippedAnswersPersisted
            if (!batchPersisted) {
              questionSubmitLog({
                event: 'question-submit:reconcile-uncommitted',
                projectId,
                answerCount: answerKeys.length,
                skippedCount: skippedQuestionIds.size,
              })
              return false
            }
          }

          handleChatEvent({ ...authoritative, type: 'state' })
          questionSubmitLog({
            event: 'question-submit:reconcile-applied',
            projectId,
            answerCount: answerKeys.length,
            status: response.status,
          })
          return true
        } catch (reconcileError) {
          questionSubmitLog({
            event: 'question-submit:reconcile-failed',
            projectId,
            answerCount: answerKeys.length,
            error: reconcileError instanceof Error
              ? reconcileError.message
              : String(reconcileError),
          })
          return false
        }
      }
      const customTextLengths = customTextLengthsSummary(answers)
      const customAnswerCount = Object.values(answers).filter(
        answer => Boolean(answer.custom_text?.trim()),
      ).length
      const selectedOptions = selectedOptionsSummary(answers)
      const isDirectorAnswer = completeQuestionBatch || answerKeys.some(
        (key) => !isSchedulingQuestionKey(key),
      )
      questionSubmitLog({
        event: 'question-submit:request',
        projectId,
        answerCount: answerKeys.length,
        answerKeys: answerKeys.join(','),
        completeQuestionBatch,
        skippedCount: skippedQuestionIds.size,
        skippedQuestionIds: [...skippedQuestionIds].join(','),
        selectedOptions,
        customTextLengths,
        hasCustomText: Boolean(customTextLengths),
        schedulingOnly: answerKeys.length > 0 && !isDirectorAnswer,
        directorAnswer: isDirectorAnswer,
      })

      // Keep a pageable batch's draft in AgentChat until the backend confirms
      // it. That lets an ambiguous transport failure preserve the user's
      // selections when the authoritative snapshot shows no commit.
      if (!deferOptimisticQuestionAnswers) setMessages((prev) => {
        const updated = prev.map((msg) => {
          if (!msg.questions) return msg
          const updatedQuestions = msg.questions.map((q) => {
            const key = q.tool_use_id
            const answerVal = key ? answers[key] : undefined
            if (answerVal) {
              return {
                ...q,
                answer: answerVal.custom_text || answerVal.selected_option,
                selected_option: answerVal.selected_option,
                custom_text: answerVal.custom_text,
              }
            }
            if (skippedQuestionIds.has(key)) {
              return {
                ...q,
                answer: '__skipped__',
                selected_option: undefined,
                custom_text: undefined,
              }
            }
            return q
          })
          if (updatedQuestions.some((q, i) => q !== msg.questions![i])) {
            return { ...msg, questions: updatedQuestions }
          }
          return msg
        })

        // Custom input still needs an optimistic user bubble
        if (answers['__custom_input__']?.custom_text) {
          updated.push({
            id: `user-${Date.now()}`,
            role: 'user',
            content: answers['__custom_input__'].custom_text,
            timestamp: new Date().toISOString(),
          })
        }
        return updated
      })

      setStatus('running')
      // Only set awaitingDirector when the answer resumes the Director
      // (i.e. not for scheduling-only questions like cost confirmation
      // or insufficient funds which only trigger worker nodes).
      if (isDirectorAnswer) {
        setAwaitingDirector(true)
        thinkingLog('respondToQuestions -> status=running, awaitingDirector=true (director answer)')
      } else {
        thinkingLog('respondToQuestions -> status=running (scheduling answer, awaitingDirector unchanged)')
      }
      setError(null)

      const logCustomAnswerResult = (accepted: boolean, responseStatus: number) => {
        if (!customTextLengths) return
        freeTextAnswerSubmitLog({
          event: accepted
            ? 'clarification:custom_answers_accepted'
            : 'clarification:custom_answers_rejected',
          trace_id: `director-free-text-answers:${projectId}`,
          projectId,
          answerKeys: answerKeys.join(','),
          submittedAnswerCount: answerKeys.length,
          customAnswerCount,
          nonemptyCustomAnswerCount: customAnswerCount,
          validationResult: accepted ? 'accepted' : 'rejected',
          requested: answerKeys.length,
          resolved: accepted ? customAnswerCount : 0,
          success_count: accepted ? customAnswerCount : 0,
          failed_count: accepted ? 0 : customAnswerCount,
          validity: accepted ? 'custom_answers_accepted' : 'custom_answers_rejected',
          status: responseStatus,
        })
      }

      let responseReceived = false
      try {
        const response = await apiFetch(
          `/projects/${projectId}/agentic/agent/respond`,
          {
            method: 'POST',
            retryTransient: 'admission-only',
            body: JSON.stringify({
              answers,
              canvas_id: canvasId,
              conversation_language: normalizeLocale(i18n.language) ?? 'en',
              ...(completeQuestionBatch ? { complete_question_batch: true } : {}),
              ...(skippedQuestionIds.size > 0
                ? { omitted_question_ids: [...skippedQuestionIds] }
                : {}),
              ...(autoConfirmCostSettings
                ? { auto_confirm_cost_settings: autoConfirmCostSettings }
                : {}),
            }),
          }
        )
        responseReceived = true

        if (!response.ok) {
          console.error('[useAgentChat] /agent/respond failed:', response.status)
          logCustomAnswerResult(false, response.status)
          const fallback = i18n.t('chat:errors.directorUnreachable')
          const message = await readActionableConnectError(response, fallback)
          i18nErrorLog({
            event: 'localized-error-banner',
            path: 'respond',
            key: 'chat:errors.directorUnreachable',
            locale: i18n.language,
            status: response.status,
          })
          throw new Error(message)
        }

        // JSON response — events come via WebSocket. Body may carry a
        // recoverable 200 ok:false signal (stale_questions /
        // answer_key_mismatch — Workplace 1554138566343441); read fields
        // defensively (no cast — repo forbids `as`).
        const data = await response.json().catch(() => null)
        questionSubmitLog({
          event: 'question-submit:response',
          projectId,
          answerCount: answerKeys.length,
          status: response.status,
          bodyOk: data?.ok,
          bodyStatus: data?.status,
          bodyReason: data?.reason,
        })
        logCustomAnswerResult(data?.ok !== false, response.status)

        // 200 ok:false signals a recoverable, non-fatal condition: either
        // (a) the submitted keys did not match what the backend now sees
        // pending (answer_key_mismatch), or (b) every submitted question
        // was stale by the time the route's pre-persist loop ran
        // (stale_questions — Workplace post 1554138566343441 — concurrent
        // compaction/truncation, two-tab race, legacy id-shape asymmetry).
        // Frontend MUST NOT show "Request failed". Fetch the authoritative
        // state because this route does not itself broadcast a replacement
        // snapshot. Without this reconciliation, rolling back a stale answer
        // makes the obsolete card interactive again and it can be resubmitted
        // forever.
        if (data && data.ok === false) {
          const reconciled = await reconcileQuestionAnswers(false)
          if (!reconciled) {
            rollbackOptimisticQuestionAnswers()
            if (isDirectorAnswer) {
              setAwaitingDirector(false)
            }
            setStatus(data.status === 'pending_questions' ? 'pending_questions' : 'idle')
          }
          return
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          rollbackOptimisticQuestionAnswers()
          return
        }
        // A transport failure is ambiguous: the backend may have committed the
        // batch before the response was lost. Reconcile first and preserve that
        // authoritative status. An explicit HTTP rejection is unambiguous and
        // should retain the normal rollback/error path.
        if (!responseReceived && await reconcileQuestionAnswers()) {
          return
        }
        rollbackOptimisticQuestionAnswers()
        questionSubmitLog({
          event: 'question-submit:error',
          projectId,
          answerCount: answerKeys.length,
          error: err instanceof Error ? err.message : String(err),
        })
        console.error('Failed to respond to agent:', err)
        setError(!responseReceived
          ? i18n.t('chat:errors.failedToRespond')
          : err instanceof Error ? err.message : i18n.t('chat:errors.failedToRespond'))
        setStatus('idle')
        setAwaitingDirector(false)
        setActiveDirectorTools({})
      }
    },
    [projectId, canvasId, i18n, autoConfirmCostSettings, handleChatEvent]
  )

  // RFC 0061: Stop the running agent execution
  // Persist the live Director's terminal verdict into the chat snapshot too,
  // so reopening an embedded chat cannot resurrect stale awaiting/stream flags.
  const reconcileDirectorTurnEnd = useCallback(() => {
    setAwaitingDirector(false)
    setActiveDirectorTools({})
    setMessages(endStreamingPlaceholders)
  }, [])

  const stop = useCallback(async () => {
    if (!projectId) throw new Error('Cannot stop execution without a project')
    // Local chat status can lag the Director. Always ask the server, and keep
    // the activity state on failure so the user can retry a real cancellation.
    const traceId = `honest-director-stop:${projectId}:${canvasId}:${++stopAttemptRef.current}`
    honestStopLog({
      event: 'stop-requested',
      trace_id: traceId,
      project_id: projectId,
      canvas_id: canvasId,
    })
    let response: Awaited<ReturnType<typeof api.cancelAgentExecution>>
    try {
      response = await api.cancelAgentExecution(projectId, canvasId)
    } catch (cause) {
      honestStopLog({
        event: 'stop-refused-http',
        trace_id: traceId,
        project_id: projectId,
        canvas_id: canvasId,
        status: cause instanceof ApiRequestError ? cause.status : undefined,
      })
      throw cause
    }
    if (response.cancelled !== true) {
      honestStopLog({
        event: 'stop-refused-legacy',
        trace_id: traceId,
        project_id: projectId,
        canvas_id: canvasId,
        reason: response.reason,
      })
      throw new Error('Stop was not confirmed. The current work may still be running.')
    }
    setAwaitingDirector(false)
    setActiveDirectorTools({})
    setMessages(sealStreamPlaceholders)
    setStatus('idle')
    honestStopLog({
      event: 'stop-confirmed',
      trace_id: traceId,
      project_id: projectId,
      canvas_id: canvasId,
    })
  }, [projectId, canvasId])

  // Delete a message by timestamp (optimistic removal + API call)
  const deleteMessage = useCallback(async (timestamp: string, content: string) => {
    if (!projectId || status === 'running') return

    setMessages((prev) => prev.filter((m) => m.timestamp !== timestamp))

    try {
      const response = await apiFetch(
        `/projects/${projectId}/agentic/agent/delete-message`,
        {
          method: 'POST',
          retryTransient: false,
          body: JSON.stringify({ timestamp, content, canvas_id: canvasId }),
        }
      )
      if (!response.ok) {
        throw new Error(`Delete failed: ${response.status}`)
      }
      const result = await response.json()
      if (!result.success) {
        console.warn('Delete message API returned:', result.reason)
      }
    } catch (err) {
      console.error('Failed to delete message:', err)
    }
  }, [projectId, canvasId, status])

  // Edit a message in place, remove dependent later turns, then replay Director.
  const editMessage = useCallback(async (timestamp: string, originalContent: string, newContent: string, referencedNodeIds?: string[]) => {
    if (!projectId || status === 'running') return

    const previousStatus = status
    const targetIndex = messages.findIndex((m) => m.timestamp === timestamp)
    if (targetIndex < 0) return

    const previousMessages = messages
    const removedMessages = messages.slice(targetIndex + 1)
    const editTraceId = createEditTraceId(projectId, timestamp)
    const traceScenario = 'edit_second_user_message_resend'
    const editedMessage: ChatMessage = {
      ...messages[targetIndex],
      content: newContent,
      referenced_node_ids:
        referencedNodeIds && referencedNodeIds.length > 0 ? referencedNodeIds : undefined,
    }
    const nextMessages = [...messages.slice(0, targetIndex), editedMessage]
    setMessages(nextMessages)

    setStatus('running')
    setAwaitingDirector(true)
    setError(null)

    editResendLog({
      event: 'chat-edit:submit',
      trace_id: editTraceId,
      layer: 'client',
      scenario: traceScenario,
      project_id: projectId,
      target_timestamp: timestamp,
      previous_status: previousStatus,
      original_length: originalContent.length,
      edited_length: newContent.length,
      original_preview: tracePreview(originalContent),
      edited_preview: tracePreview(newContent),
      referenced_node_count: referencedNodeIds?.length ?? 0,
    })

    editResendLog({
      event: 'chat-edit:client_optimistic_rewind_applied',
      trace_id: editTraceId,
      layer: 'client',
      scenario: traceScenario,
      project_id: projectId,
      target_timestamp: timestamp,
      target_message_index: targetIndex,
      ui_message_count_before: previousMessages.length,
      ui_message_count_after: nextMessages.length,
      removed_message_count: removedMessages.length,
      retained_roles: traceRoles(nextMessages),
      removed_conversation_roles: traceRoles(removedMessages),
      status_before: previousStatus,
      status_after: 'running',
    })

    try {
      const body: {
        timestamp: string
        content: string
        original_content: string
        trace_id: string
        referenced_node_ids?: string[]
        conversation_language?: string
        canvas_id: string
        auto_confirm_cost_settings?: AutoConfirmCostSettings
      } = {
        timestamp,
        content: newContent,
        original_content: originalContent,
        trace_id: editTraceId,
        canvas_id: canvasId,
        // Keep the edited-turn replay in the same Director conversation language.
        conversation_language: normalizeLocale(i18n.language) ?? 'en',
      }
      if (referencedNodeIds && referencedNodeIds.length > 0) {
        body.referenced_node_ids = referencedNodeIds
      }
      if (autoConfirmCostSettings) {
        body.auto_confirm_cost_settings = autoConfirmCostSettings
      }

      const response = await apiFetch(
        `/projects/${projectId}/agentic/agent/edit-message`,
        {
          method: 'POST',
          retryTransient: false,
          body: JSON.stringify(body),
        },
      )
      if (!response.ok) {
        console.error('[useAgentChat] edit/resend failed:', response.status)
        throw new Error(i18n.t('chat:errors.editSaveFailed'))
      }
      const result = await response.json()
      if (!result.success) {
        throw new Error(result.reason ?? i18n.t('chat:errors.editSaveFailed'))
      }
      editResendLog({
        event: 'chat-edit:server_edit_request_accepted',
        trace_id: editTraceId,
        layer: 'client',
        scenario: traceScenario,
        project_id: projectId,
        target_timestamp: timestamp,
        api_status: result.status ?? 'unknown',
        rewound_after_edit: result.rewound_after_edit === true,
      })
    } catch (err) {
      console.error('Failed to edit message:', err)
      editResendLog({
        event: 'chat-edit:client_restore_after_edit_failed',
        trace_id: editTraceId,
        layer: 'client',
        scenario: traceScenario,
        project_id: projectId,
        target_timestamp: timestamp,
        restored_message_count: previousMessages?.length ?? 0,
        restored_status: previousStatus,
        error_message: err instanceof Error ? err.message : 'Failed to edit message',
      })
      if (previousMessages) {
        setMessages(previousMessages)
      }
      setError(err instanceof Error ? err.message : i18n.t('chat:errors.failedToEdit'))
      setStatus(previousStatus)
      setAwaitingDirector(false)
      setActiveDirectorTools({})
    }
  }, [projectId, canvasId, status, messages, i18n, autoConfirmCostSettings])

  const dismissPendingQuestions = useCallback((partialAnswers?: Record<string, string>) => {
    setMessages((prev) =>
      prev.map((msg) => {
        if (!msg.questions) return msg
        const updated = msg.questions.map((q) => {
          if (q.answer) return q
          const partial = partialAnswers?.[q.tool_use_id]
          if (partial) {
            return { ...q, answer: `User selected: ${partial}` }
          }
          return { ...q, answer: '__skipped__' }
        })
        if (updated.some((q, i) => q !== msg.questions![i])) {
          return { ...msg, questions: updated }
        }
        return msg
      }),
    )
  }, [])

  return {
    messages,
    firstUnansweredMessageIndex,
    status,
    error,
    isLoading,
    isRestoredSession,
    awaitingDirector,
    chatSilentMs,
    activeDirectorToolCount,
    // Chat WebSocket lifecycle — surfaced so callers can reconcile REST-only
    // state (e.g. the canvas tab list) whenever the socket (re)connects. The
    // canvas_created broadcast is not queued for late subscribers, so a full
    // REST refetch on connect is the safety net for broadcasts missed while
    // AgentChat was unmounted or the WS was still handshaking.
    chatSocketConnected,
    // A dropped chat socket is never surfaced as an error: callers show a
    // neutral indicator while `reconnecting`, and the reconnect `state`
    // snapshot reconciles the turn. Only backend-reported failures set
    // `error` / `status === 'error'`.
    chatConnectionState,
    startWithPrompt,
    respondToQuestions,
    dismissPendingQuestions,
    deleteMessage,
    editMessage,
    stop,
    reconcileDirectorTurnEnd,
  }
}
